import { format } from "date-fns";
import { enCA, frCA } from "date-fns/locale";
import { formatInTimeZone } from "date-fns-tz";
import React, { FunctionComponent } from "react";

import { useLanguage } from "../../i18n/useLanguage";
import { useNullableDealerAccountInfo } from "../../stores/hooks/useNullableDealerAccountInfo";

const DATE_FORMAT = "date" as const;
const DATE_TIME_FORMAT = "datetime" as const;
export const DEALER_DATE_TIME = "localeDateTime" as const;
export const TIME_FORMAT = "time" as const;

type DateDisplayProps = {
  children?: Date | string;
  format?:
    | typeof DATE_FORMAT
    | typeof DATE_TIME_FORMAT
    | typeof DEALER_DATE_TIME
    | typeof TIME_FORMAT;
};

export const formatDate = (
  dateString: Date | string,
  language: string
): string => {
  const date = new Date(dateString);
  const locale = language === "fr" ? "fr-CA" : "en-CA";

  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
  };

  return new Intl.DateTimeFormat(locale, options).format(date);
};

export const formatDateTime = (
  dateString: Date | string,
  language: string
): string => {
  const date = new Date(dateString);
  const formatString = "d MMM yyyy, h:mm a";
  const locale = language === "fr" ? frCA : enCA;
  return format(date, formatString, { locale });
};

export const formatTime = (
  dateString: Date | string,
  language: string
): string => {
  const date = new Date(dateString);
  const formatString = "h:mm:ss a";
  const locale = language === "fr" ? frCA : enCA;
  return format(date, formatString, { locale });
};

export const formatLocaleDateTime = (
  dateString: Date | string,
  language: string,
  timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone
): string => {
  const date = new Date(dateString);
  const formatString = "d MMM yyyy, h:mm a zzz";
  const locale = language === "fr" ? frCA : enCA;
  return formatInTimeZone(date, timeZone, formatString, { locale });
};

export const DateDisplay: FunctionComponent<DateDisplayProps> = ({
  children,
  format = DATE_FORMAT,
}) => {
  const [language] = useLanguage();

  const dealer = useNullableDealerAccountInfo();

  if (!children) {
    return null;
  }

  switch (format) {
    case DATE_FORMAT:
      return <>{formatDate(children, language)}</>;
    case TIME_FORMAT:
      return <>{formatTime(children, language)}</>;
    case DEALER_DATE_TIME:
      return (
        <>{formatLocaleDateTime(children, language, dealer?.dealerTimeZone)}</>
      );
    default:
      return <>{formatDateTime(children, language)}</>;
  }
};
